import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, interval } from 'rxjs';
import { tap, catchError, switchMap, filter, take, timeout } from 'rxjs/operators';
import { Global } from '../global';

export interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  isLoading?: boolean;
  error?: boolean;
}

export interface ChatRequest {
  message: string;
  sessionId?: string;
  context?: ChatContext;
}

export interface ChatContext {
  nit?: string;
  documentType?: string;
  currentPage?: string;
  userRole?: string;
  filters?: any;
  searchQuery?: string;
}

export interface ChatResponse {
  success: boolean;
  message: string;
  sessionId: string;
  timestamp?: string;
  error?: string;
  errorType?: string;
  metadata?: {
    processingTime: number;
    statusCode: number;
  };
}

export interface ChatKpisJobResponse {
  jobId: string;
  status: string;
  sessionId: string;
}

export interface ChatKpisStatusResponse extends ChatResponse {
  jobId: string;
  status: 'pending' | 'done' | 'error';
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private API_URL: string;
  private readonly messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  public messages$ = this.messagesSubject.asObservable();
  private currentSessionId: string | null = null;
  private currentSessionIdCdt: string | null = null;
  private readonly connectionStatusSubject = new BehaviorSubject<boolean>(true);
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  private readonly MESSAGES_STORAGE_KEY = 'chat_messages';
  private readonly SESSION_STORAGE_KEY = 'chat_session_id';

  constructor(private readonly http: HttpClient) {
    this.API_URL = Global.url + '/chat';
    this.loadMessagesFromStorage();
  }

  /**
   * Starts a fresh NORMAL chat session (new server-side context) and persists it.
   * Intentionally does NOT clear the locally persisted message history.
   */
  startNewSession(): void {
    this.currentSessionId = crypto.randomUUID();
    try {
      localStorage.setItem(this.SESSION_STORAGE_KEY, this.currentSessionId);
    } catch {
      // ignore storage errors
    }
  }

  /**
   * Starts a fresh CDT chat session (new server-side context).
   */
  startNewSessionCdt(): void {
    this.currentSessionIdCdt = crypto.randomUUID();
  }

  /**
   * Session hygiene: regenerate BOTH chat session ids (normal + CDT) so the next
   */
  refreshAllSessions(): void {
    this.startNewSession();
    this.startNewSessionCdt();
  }

  /**
   * Entry-point reset for the component (NORMAL chat only).
   * Clears persisted messages and regenerates a fresh sessionId.
   */
  resetNormalChatForComponentEntry(): void {
    this.messagesSubject.next([]);

    try {
      localStorage.removeItem(this.MESSAGES_STORAGE_KEY);
    } catch {
      // ignore storage errors
    }

    this.startNewSession();
  }

  public setApiUrl(url: string) {
    this.API_URL = url + '/chat';
  }

  sendMessage(message: string, context?: ChatContext): Observable<ChatKpisStatusResponse> {
    if (!message?.trim()) {
      return throwError(() => new Error('El mensaje no puede estar vacio'));
    }

    const userMessage: ChatMessage = {
      id: this.generateId(),
      content: message,
      sender: 'user',
      timestamp: new Date()
    };
    this.addMessage(userMessage);

    const loadingMessage: ChatMessage = {
      id: this.generateId(),
      content: 'Pensando...',
      sender: 'bot',
      timestamp: new Date(),
      isLoading: true
    };
    this.addMessage(loadingMessage);

    const body: ChatRequest = {
      message: message.trim(),
      sessionId: this.currentSessionId ?? undefined,
      context: context || {}
    };
    const endpointUrl = Global.url + '/chat_documentos_vinculacion';

    return this.http.post<ChatKpisJobResponse>(`${endpointUrl}/message`, body, { headers: this.getAuthHeaders() })
      .pipe(
        switchMap(jobResponse => {
          this.currentSessionId = jobResponse.sessionId;
          return interval(2500).pipe(
            switchMap(() =>
              this.http.get<ChatKpisStatusResponse>(
                `${endpointUrl}/status/${jobResponse.jobId}`,
                { headers: this.getAuthHeaders() }
              )
            ),
            filter(status => status.status === 'done' || status.status === 'error'),
            take(1),
            timeout(180000)
          );
        }),
        tap(response => {
          this.removeMessage(loadingMessage.id);
          let finalMessage = response.message || 'Sin respuesta';
          const shouldResetSession = finalMessage.includes('[RESPALDO_OK]');
          if (shouldResetSession) {
            finalMessage = finalMessage.replace('[RESPALDO_OK]', '').trim();
          }
          const botMessage: ChatMessage = {
            id: this.generateId(),
            content: finalMessage,
            sender: 'bot',
            timestamp: new Date()
          };
          this.addMessage(botMessage);
          if (response.sessionId) {
            this.currentSessionId = response.sessionId;
          }

          // IMPORTANT: reset AFTER applying response.sessionId, otherwise it gets overwritten.
          if (shouldResetSession) {
            this.startNewSession();
          }
          this.connectionStatusSubject.next(true);
          this.saveMessagesToStorage();
        }),
        catchError((error) => {
          this.removeMessage(loadingMessage.id);
          let content: string;
          if (error?.name === 'TimeoutError') {
            content = 'El agente está tardando demasiado. Por favor, intenta de nuevo.';
          } else if (error instanceof HttpErrorResponse) {
            content = this.getErrorMessage(error);
          } else {
            content = 'Ocurrió un error al procesar tu mensaje. Por favor, intenta de nuevo.';
          }
          const errorMessage: ChatMessage = {
            id: this.generateId(),
            content,
            sender: 'bot',
            timestamp: new Date(),
            error: true
          };
          this.addMessage(errorMessage);
          this.connectionStatusSubject.next(false);
          return throwError(() => error);
        })
      );
  }

  sendMessageCdt(message: string): Observable<ChatKpisStatusResponse> {
    if (!message?.trim()) {
      return throwError(() => new Error('El mensaje no puede estar vacio'));
    }

    const userMessage: ChatMessage = {
      id: this.generateId(),
      content: message,
      sender: 'user',
      timestamp: new Date()
    };
    this.addMessage(userMessage);

    const loadingMessage: ChatMessage = {
      id: this.generateId(),
      content: 'Pensando...',
      sender: 'bot',
      timestamp: new Date(),
      isLoading: true
    };
    this.addMessage(loadingMessage);

    const body = {
      message: message.trim(),
      sessionId: this.currentSessionIdCdt ?? undefined
    };
    const endpointUrl = Global.url + '/chat_kpis_cdt_ahorro';

    return this.http.post<ChatKpisJobResponse>(`${endpointUrl}/message`, body, { headers: this.getAuthHeaders() })
      .pipe(
        switchMap(jobResponse => {
          this.currentSessionIdCdt = jobResponse.sessionId;
          return interval(2500).pipe(
            switchMap(() =>
              this.http.get<ChatKpisStatusResponse>(
                `${endpointUrl}/status/${jobResponse.jobId}`,
                { headers: this.getAuthHeaders() }
              )
            ),
            filter(status => status.status === 'done' || status.status === 'error'),
            take(1),
            timeout(180000)
          );
        }),
        tap(response => {
          this.removeMessage(loadingMessage.id);
          const botMessage: ChatMessage = {
            id: this.generateId(),
            content: response.message || 'Sin respuesta',
            sender: 'bot',
            timestamp: new Date()
          };
          this.addMessage(botMessage);
          if (response.sessionId) {
            this.currentSessionIdCdt = response.sessionId;
          }
          this.connectionStatusSubject.next(true);
          this.saveMessagesToStorage();
        }),
        catchError((error) => {
          this.removeMessage(loadingMessage.id);
          let content: string;
          if (error?.name === 'TimeoutError') {
            content = 'El agente está tardando demasiado. Por favor, intenta de nuevo.';
          } else if (error instanceof HttpErrorResponse) {
            content = this.getErrorMessage(error);
          } else {
            content = 'Ocurrió un error al procesar tu mensaje. Por favor, intenta de nuevo.';
          }
          const errorMessage: ChatMessage = {
            id: this.generateId(),
            content,
            sender: 'bot',
            timestamp: new Date(),
            error: true
          };
          this.addMessage(errorMessage);
          this.connectionStatusSubject.next(false);
          return throwError(() => error);
        })
      );
  }

  clearChat(): void {
    this.messagesSubject.next([]);
    this.clearMessagesFromStorage();
    // Session hygiene: emptying the trash must start a brand-new context.
    // Regenerate fresh session ids instead of leaving them null.
    this.refreshAllSessions();
  }

  getSessionId(): string | null {
    return this.currentSessionId;
  }

  checkHealth(): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.get(`${Global.url}/chat_documentos_vinculacion/health`, { headers })
      .pipe(
        tap(() => {
          this.connectionStatusSubject.next(true);
        }),
        catchError((error: HttpErrorResponse) => {
          this.connectionStatusSubject.next(false);
          return throwError(() => error);
        })
      );
  }

  checkHealthCdt(): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.get(`${Global.url}/chat_kpis_cdt_ahorro/health`, { headers })
      .pipe(
        tap(() => {
          this.connectionStatusSubject.next(true);
        }),
        catchError((error: HttpErrorResponse) => {
          this.connectionStatusSubject.next(false);
          return throwError(() => error);
        })
      );
  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
  }

  private addMessage(message: ChatMessage): void {
    const current = this.messagesSubject.value;
    this.messagesSubject.next([...current, message]);
  }

  private removeMessage(id: string): void {
    const current = this.messagesSubject.value;
    this.messagesSubject.next(current.filter(m => m.id !== id));
  }

  private generateId(): string {
    return `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error.error?.error) {
      return error.error.error;
    }
    switch (error.status) {
      case 0: return 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
      case 401: return 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
      case 429: return 'Has enviado demasiados mensajes. Por favor, espera un momento.';
      case 500: return 'El agente está experimentando problemas. Por favor, intenta de nuevo.';
      case 503: return 'El servicio de chat no está disponible en este momento.';
      default: return 'Ocurrió un error al procesar tu mensaje. Por favor, intenta de nuevo.';
    }
  }

  private saveMessagesToStorage(): void {
    try {
      const messages = this.messagesSubject.value.slice(-50);
      localStorage.setItem(this.MESSAGES_STORAGE_KEY, JSON.stringify(messages));
      if (this.currentSessionId) {
        localStorage.setItem(this.SESSION_STORAGE_KEY, this.currentSessionId);
      }
    } catch (e) {
      console.warn('No se pudieron guardar los mensajes:', e);
    }
  }

  private loadMessagesFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.MESSAGES_STORAGE_KEY);
      const sessionId = localStorage.getItem(this.SESSION_STORAGE_KEY);
      if (stored) {
        const messages = JSON.parse(stored);
        messages.forEach((m: ChatMessage) => {
          m.timestamp = new Date(m.timestamp);
        });
        this.messagesSubject.next(messages);
      }
      if (sessionId) {
        this.currentSessionId = sessionId;
      }
    } catch (e) {
      console.warn('No se pudieron cargar los mensajes:', e);
    }
  }

  private clearMessagesFromStorage(): void {
    try {
      localStorage.removeItem(this.MESSAGES_STORAGE_KEY);
      localStorage.removeItem(this.SESSION_STORAGE_KEY);
    } catch (e) {
      console.warn('No se pudieron limpiar los mensajes:', e);
    }
  }
}
